import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { bookings, timeSlots, users, waitlist } from '../../services/api';

/*
 * Marcar uma aula ao balcão, em nome do membro.
 *
 * Porque existe: a API de reservas era só self-service (tirava o utilizador do token) e o botão
 * «Marcar aula» da Operação/Receção aparecia desativado a dizer que a marcação ao balcão ainda não
 * existia. Passa a existir em POST /api/bookings/on-behalf, com as MESMAS regras da marcação feita
 * pelo próprio — horário bloqueado, lotação, marcação repetida e limite diário.
 *
 * Os horários que a receção vê são os que o atleta vê (os que o ginásio liberou). Uma aula cheia não
 * é um beco sem saída: o servidor responde «slot-full» e aqui oferece-se a lista de espera que o
 * ginásio já tem. Nada disto cobra, marca presença ou notifica ninguém — a aplicação não faz isso
 * numa marcação, e este ecrã não inventa.
 */

const OUTCOMES = {
  'slot-blocked': 'Este horário está bloqueado pelo ginásio.',
  'already-booked': 'Este membro já tem marcação neste horário.',
  'daily-limit-reached': 'Este membro já atingiu o limite de marcações desse dia.',
  'member-inactive': 'A conta deste membro está inativa.',
  'member-not-found': 'O membro já não existe nesta base.',
  'slot-not-found': 'Este horário já não existe.',
  'already-on-waitlist': 'Este membro já está na lista de espera deste horário.',
  'waitlist-disabled': 'A lista de espera está desligada neste ginásio.',
  'waitlist-full': 'A lista de espera deste horário está cheia.',
};

const fmtDay = (d) => new Date(d).toLocaleDateString('pt-PT', { weekday: 'short', day: '2-digit', month: 'short' });
const fmtHour = (d) => new Date(d).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });

export default function BookClassDialog({ open, onClose, onDone }) {
  const [all, setAll] = useState([]);
  const [term, setTerm] = useState('');
  const [selected, setSelected] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsError, setSlotsError] = useState(false);
  const [slot, setSlot] = useState(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);   // { text, canWaitlist }

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setTerm(''); setSelected(null); setSlot(null); setNotes(''); setProblem(null); setSlotsError(false);
    users.getAll()
      .then((r) => { if (alive) setAll(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setAll([]); });
    timeSlots.getAll()
      .then((r) => { if (alive) setSlots(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setSlotsError(true); });
    return () => { alive = false; };
  }, [open]);

  const matches = useMemo(() => {
    const q = term.trim().toLowerCase();
    if (q.length < 2) return [];
    return all
      .filter((u) => (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q))
      .slice(0, 6);
  }, [term, all]);

  // Só o que ainda não começou, pela ordem em que acontece.
  const upcoming = useMemo(() => {
    const now = Date.now();
    return slots
      .filter((s) => new Date(s.startTime).getTime() > now)
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
      .slice(0, 8);
  }, [slots]);

  if (!open) return null;

  const send = async (toWaitlist) => {
    if (!selected) { toast.warning('Escolha o membro'); return; }
    if (!slot) { toast.warning('Escolha o horário'); return; }
    setBusy(true); setProblem(null);
    try {
      const call = toWaitlist ? waitlist.onBehalf : bookings.onBehalf;
      const res = await call({ userId: selected.id, timeSlotId: slot.id, notes: notes.trim() || null });
      toast.success(res.data?.message || (toWaitlist ? 'Ficou na lista de espera' : 'Aula marcada'));
      if (onDone) onDone();
      onClose();
    } catch (e) {
      const d = e.response?.data || {};
      const code = d.outcome;
      setProblem({
        text: d.message || OUTCOMES[code] || 'Não foi possível concluir a marcação.',
        canWaitlist: code === 'slot-full',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={S.backdrop} role="dialog" aria-modal="true" aria-label="Marcar aula ao balcão">
      <div style={S.panel}>
        <div style={S.head}>
          <h3 style={S.title}>Marcar aula</h3>
          <button type="button" style={S.close} onClick={onClose} aria-label="Fechar">✕</button>
        </div>

        <label style={S.label} htmlFor="bk-search">Membro</label>
        <input id="bk-search" style={S.input} value={term} onChange={(e) => { setTerm(e.target.value); setSelected(null); }}
               placeholder="Nome ou email (mínimo 2 letras)" autoFocus />
        {selected
          ? <div style={S.picked}>A marcar para <strong>{selected.name}</strong> · <button type="button" style={S.link} onClick={() => { setSelected(null); setTerm(''); }}>trocar</button></div>
          : term.trim().length >= 2 && matches.length === 0 && <div style={S.hint}>Sem resultados.</div>}
        {!selected && matches.length > 0 && (
          <ul style={S.list}>
            {matches.map((u) => (
              <li key={u.id}>
                <button type="button" style={S.option} onClick={() => { setSelected(u); setTerm(u.name || ''); setProblem(null); }}>
                  <span style={S.optionName}>{u.name || '(sem nome)'}</span>
                  <span style={S.optionMeta}>{u.email || ''}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <label style={S.label}>Horário</label>
        {slotsError && <div style={S.error}>Não foi possível carregar os horários. Feche e tente outra vez.</div>}
        {!slotsError && upcoming.length === 0 && (
          <div style={S.hint}>Não há horários futuros liberados. Liberte horários em Horários ou confirme as datas.</div>
        )}
        {!slotsError && upcoming.length > 0 && (
          <ul style={S.list}>
            {upcoming.map((s) => {
              const free = Math.max(0, (s.maxCapacity || 0) - (s.currentBookings || 0));
              return (
                <li key={s.id}>
                  <button type="button" style={{ ...S.option, ...(slot?.id === s.id ? S.optionActive : {}) }} onClick={() => { setSlot(s); setProblem(null); }}>
                    <span style={S.optionName}>{fmtDay(s.startTime)} · {fmtHour(s.startTime)}–{fmtHour(s.endTime)}</span>
                    <span style={S.optionMeta}>
                      {s.isFull ? 'Cheio' : `${free} de ${s.maxCapacity} lugares`}
                      {s.isFull ? ' — só lista de espera' : ''}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <label style={S.label} htmlFor="bk-notes">Nota (opcional)</label>
        <input id="bk-notes" style={S.input} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="ex.: marcado ao telefone" />

        {problem && (
          <div style={S.error2}>
            <strong>Não marcado</strong>
            <div style={S.reason}>{problem.text}</div>
          </div>
        )}

        <div style={S.footer}>
          <button type="button" style={S.secondary} onClick={onClose}>Cancelar</button>
          {/* Aula já cheia na lista: a lista de espera é o único caminho — não se espera por uma recusa para a oferecer. */}
          {(problem?.canWaitlist || slot?.isFull) && (
            <button type="button" style={S.secondary} disabled={busy || !selected} onClick={() => send(true)}>Pôr na lista de espera</button>
          )}
          <button type="button" style={S.primary} disabled={busy || slot?.isFull} onClick={() => send(false)}>
            {busy ? 'A marcar…' : 'Marcar'}
          </button>
        </div>
      </div>
    </div>
  );
}

const S = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1000 },
  panel: { background: 'var(--bg-card)', color: 'var(--text-primary)', borderRadius: 'var(--radius-lg, 14px)', padding: 20, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-lg, 0 20px 40px rgba(0,0,0,.2))' },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  title: { margin: 0, fontSize: 18, fontWeight: 700 },
  close: { border: 'none', background: 'none', fontSize: 16, cursor: 'pointer', color: 'var(--text-secondary)' },
  label: { display: 'block', fontSize: 13, fontWeight: 600, margin: '12px 0 6px', color: 'var(--text-secondary)' },
  input: { width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md, 8px)', border: '1px solid var(--border-color)', background: 'var(--bg-input, var(--bg-card))', color: 'var(--text-primary)', fontSize: 14 },
  list: { listStyle: 'none', margin: '10px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 6 },
  option: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: 'var(--radius-md, 8px)', border: '1px solid var(--border-color)', background: 'var(--bg-card)', cursor: 'pointer' },
  optionActive: { borderColor: 'var(--accent-primary)', background: 'var(--bg-hover)' },
  optionName: { fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' },
  optionMeta: { fontSize: 12, color: 'var(--text-secondary)' },
  picked: { fontSize: 13, color: 'var(--text-secondary)', marginTop: 8 },
  link: { border: 'none', background: 'none', color: 'var(--accent-primary)', cursor: 'pointer', fontSize: 13, padding: 0, textDecoration: 'underline' },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 },
  primary: { padding: '10px 14px', borderRadius: 'var(--radius-md, 8px)', border: '1px solid var(--border-color)', background: 'var(--accent-primary)', color: 'var(--text-inverse)', fontSize: 14, cursor: 'pointer' },
  secondary: { padding: '10px 14px', borderRadius: 'var(--radius-md, 8px)', border: '1px solid var(--border-color)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 14, cursor: 'pointer' },
  hint: { fontSize: 13, color: 'var(--text-muted)', marginTop: 8 },
  error: { fontSize: 13, color: 'var(--error-text)', marginTop: 8 },
  error2: { border: '1px solid var(--error)', background: 'var(--error-bg)', color: 'var(--error-text)', borderRadius: 'var(--radius-md, 8px)', padding: 12, fontSize: 14, marginTop: 12 },
  reason: { fontSize: 13, marginTop: 4 },
};
